"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { FiPlay, FiUsers } from "react-icons/fi"
import CustomSelect from "../../../ui/custom-select"
import api from "../../../libs/axios"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"

interface Props {
  onComplete: (data: any) => void
  onBack: () => void
  matchData: any
}

export default function StartInnings({ onComplete, onBack, matchData }: Props) {
  console.log(matchData)
  const [formData, setFormData] = useState({
    striker_id: matchData.striker_id || "",
    non_striker_id: matchData.non_striker_id || "",
    bowler_id: matchData.bowler_id || "",
    bowling_style_id: matchData.bowling_style_id || "",
  })

  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [battingTeamPlayers, setBattingTeamPlayers] = useState<any[]>([])
  const [bowlingTeamPlayers, setBowlingTeamPlayers] = useState<any[]>([])
  const [bowlingStyles, setBowlingStyles] = useState<any[]>([])
  const [battingTeamName, setBattingTeamName] = useState("")
  const [bowlingTeamName, setBowlingTeamName] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    if (!matchData) return
    setFormData((prev) => ({
      ...prev,
      striker_id: matchData.striker_id != null ? matchData.striker_id.toString() : prev.striker_id,
      non_striker_id: matchData.non_striker_id != null ? matchData.non_striker_id.toString() : prev.non_striker_id,
      bowler_id: matchData.bowler_id != null ? matchData.bowler_id.toString() : prev.bowler_id,
      bowling_style_id:
        matchData.bowling_style_id != null ? matchData.bowling_style_id.toString() : prev.bowling_style_id,
    }))
  }, [matchData])

  useEffect(() => {
    // Determine batting and bowling teams based on toss decision
    const decision = (matchData?.toss_decision || "").toString().toUpperCase()
    const teamAId = Number(matchData.team_a_id)
    const teamBId = Number(matchData.team_b_id)
    const tossWinnerId = Number(matchData.toss_winner_team_id)

    let battingTeamId: number
    let bowlingTeamId: number

    if (decision === "BAT") {
      battingTeamId = tossWinnerId
      bowlingTeamId = battingTeamId === teamAId ? teamBId : teamAId
    } else {
      bowlingTeamId = tossWinnerId
      battingTeamId = bowlingTeamId === teamAId ? teamBId : teamAId
    }

    // Set team names
    setBattingTeamName(battingTeamId === teamAId ? "Team A" : "Team B")
    setBowlingTeamName(bowlingTeamId === teamAId ? "Team A" : "Team B")

    // Get batting team players (playing eleven only)
    const battingPlayers = (battingTeamId === teamAId ? matchData.team_a_players : matchData.team_b_players)
      .filter((p: any) => !p.is_12th_man)
      .map((p: any) => ({
        value: p.player_id,
        label: p.player_name,
        description: p.is_captain ? "Captain" : p.is_wicket_keeper ? "Wicket Keeper" : "Player",
      }))

    // Get bowling team players (playing eleven only)
    const bowlingPlayers = (bowlingTeamId === teamAId ? matchData.team_a_players : matchData.team_b_players)
      .filter((p: any) => !p.is_12th_man)
      .map((p: any) => ({
        value: p.player_id,
        label: p.player_name,
        description: p.is_captain ? "Captain" : p.is_wicket_keeper ? "Wicket Keeper" : "Player",
      }))

    setBattingTeamPlayers(battingPlayers)
    setBowlingTeamPlayers(bowlingPlayers)
  }, [matchData])

  const validateForm = () => {
    const newErrors: any = {}

    if (!formData.striker_id) {
      newErrors.striker_id = "Striker is required"
    }

    if (!formData.non_striker_id) {
      newErrors.non_striker_id = "Non-striker is required"
    }

    if (!formData.bowler_id) {
      newErrors.bowler_id = "Bowler is required"
    }

    if (!formData.bowling_style_id) {
      newErrors.bowling_style_id = "Bowling style is required"
    }

    // Validate striker and non-striker are different
    if (formData.striker_id && formData.non_striker_id && formData.striker_id === formData.non_striker_id) {
      newErrors.non_striker_id = "Striker and non-striker cannot be the same player"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      await api.put(`/matches/${matchData.match_id}/start-innings`, {
        striker_id: Number.parseInt(formData.striker_id.toString()),
        non_striker_id: Number.parseInt(formData.non_striker_id.toString()),
        bowler_id: Number.parseInt(formData.bowler_id.toString()),
        bowling_style_id: Number.parseInt(formData.bowling_style_id.toString()),
      })

      onComplete({ ...formData, match_started: true })
      toast.success("Match started successfully! The match is now LIVE.")
      navigate("/matches/view", { state: { matchId: matchData.match_id } })
    } catch (error: any) {
      toast.error(error.response.data.message || "Failed to start match")
      console.error("Network Error:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBowlingStyle()
  }, [])

  const fetchBowlingStyle = async () => {
    try {
      const response = await api.get("/dropdown?category=bowling_style")
      setBowlingStyles(response.data.data)
    } catch (error: any) {
      console.error("Error:", error)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Match</h2>
        <p className="text-gray-600">Select opening batsmen and bowler to begin the innings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Batting Team Section */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
            <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
              <FiUsers className="w-5 h-5 mr-2" />
              {battingTeamName} - Batting
            </h3>

            <div className="space-y-4">
              <CustomSelect
                label="Opening Striker"
                options={battingTeamPlayers}
                value={formData.striker_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, striker_id: value }))}
                placeholder="Select opening striker"
                error={errors.striker_id}
                required
                searchable
              />

              <CustomSelect
                label="Opening Non-Striker"
                options={battingTeamPlayers.filter((p) => p.value.toString() !== formData.striker_id.toString())}
                value={formData.non_striker_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, non_striker_id: value }))}
                placeholder="Select opening non-striker"
                error={errors.non_striker_id}
                required
                searchable
              />
            </div>
          </div>

          {/* Bowling Team Section */}
          <div className="bg-red-50 rounded-lg p-6 border border-red-200">
            <h3 className="text-lg font-medium text-red-900 mb-4 flex items-center">
              <FiUsers className="w-5 h-5 mr-2" />
              {bowlingTeamName} - Bowling
            </h3>

            <div className="space-y-4">
              <CustomSelect
                label="Opening Bowler"
                options={bowlingTeamPlayers}
                value={formData.bowler_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, bowler_id: value }))}
                placeholder="Select opening bowler"
                error={errors.bowler_id}
                required
                searchable
              />

              <CustomSelect
                label="Bowling Style"
                options={
                  bowlingStyles?.map((bowlingStyle) => ({
                    value: bowlingStyle.id,
                    label: bowlingStyle.name,
                  })) || []
                }
                value={formData.bowling_style_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, bowling_style_id: value }))}
                placeholder="Select bowling style"
                error={errors.bowling_style_id}
                required
                searchable
              />
            </div>
          </div>
        </div>

        {/* Match Summary */}
        {formData.striker_id && formData.non_striker_id && formData.bowler_id && (
          <div className="bg-gray-50 rounded-lg p-6 border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Match Ready to Start</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-700">Striker</p>
                <p className="text-gray-600">
                  {battingTeamPlayers.find((p) => p.value.toString() === formData.striker_id.toString())?.label}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Non-Striker</p>
                <p className="text-gray-600">
                  {battingTeamPlayers.find((p) => p.value.toString() === formData.non_striker_id.toString())?.label}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Bowler</p>
                <p className="text-gray-600">
                  {bowlingTeamPlayers.find((p) => p.value.toString() === formData.bowler_id.toString())?.label}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Back to Toss
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <FiPlay className="w-4 h-4 mr-2" />
            {loading ? "Starting Match..." : "Start Match"}
          </button>
        </div>
      </form>
    </div>
  )
}
